import { ResolvableService } from '@ispm/core/services/resolvable.service';
import { ElementRef, Output, EventEmitter, ChangeDetectorRef, Component, ComponentFactoryResolver, ViewChild, ViewContainerRef, ChangeDetectionStrategy, Input, OnDestroy, AfterContentInit } from '@angular/core';
import { IdynamicFormItem } from '../../interfaces/IdynamicFormItem';
import { FormControl, AbstractControl, AsyncValidator, FormGroup, ValidationErrors } from '@angular/forms';
import { get, isEqual } from 'lodash';
import { DynamicFormModalPayload } from '@ispm/dynamic-form/classes/dynamic-form-modal-payload.class';
import { WidgetValueWrapper } from '@ispm/dynamic-form/classes/widget-value-wrapper.class';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { fromEvent, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { IoptionElemet } from '@ispm/shared/interfaces/IoptionElemet';

@Component({
  selector: 'gen-widget-item',
  templateUrl: './widget-item.component.html',
  styleUrls: ['./widget-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class WidgetItemComponent implements AfterContentInit, OnDestroy {
  private _submitted: boolean;

  private _value: any;

  private _componentRef: any;

  private _destroyer: Subject<void> = new Subject();

  @Output()
  public onAddControl: EventEmitter<IdynamicFormItem> = new EventEmitter();

  @Output()
  public action: EventEmitter<IWidgetAction> = new EventEmitter();

  @Output()
  public onFocus: EventEmitter<Event> = new EventEmitter<Event>();

  @Output()
  public onBlur: EventEmitter<Event> = new EventEmitter<Event>();

  @Output()
  public forceUpdateEmitter: EventEmitter<boolean> = new EventEmitter();

  @Output()
  public submitUpdateEmitter: EventEmitter<boolean> = new EventEmitter();

  @Input()
  public pathToField: string;

  @Input()
  set value(value: any) {
    this._value = value;
    this.setComponentData();

    if(this._componentRef) {
      this.setComponentValidator();
    }
  }

  get value() {
    return this._value;
  }

  @Input()
  set disabled(disabled: boolean) {
    this._disabled = disabled;
    this.setWidgetDisabledStatus();
  }

  @Input()
  set formInstance(v: FormGroup) {
    // When the dynamic form instance is re-initialized we need to update the widgets with the new form instance
    this._formInstance = v;
    this.setWidgetFormInstance();
  }

  @Input()
  set options(value: IoptionElemet[] | any[]) {
    this._options = value;
    this.setComponentData();
  }


  get formInstance() {
    return this._formInstance;
  }

  @Input()
  set submitted(submitted: boolean) {
    // When the dynamic form instance is re-initialized we need to update the widgets with the new form instance
    this._submitted = submitted;
    this.setWidgetSubmitted();
  }

  get submitted() {
    return this._submitted;
  }

  @Input()
  public formId;

  private _formInstance: FormGroup;

  private _formBaseWidgetPayload: DynamicFormModalPayload;

  private _item: IdynamicFormItem;

  private _options: IoptionElemet[] | any[];

  @Input()
  set formBaseWidgetPayload(dynamicFormModalPayload: DynamicFormModalPayload) {
    this._formBaseWidgetPayload = dynamicFormModalPayload;
    const globalConfigurations = this._formBaseWidgetPayload.globalConfigurations;

    if(globalConfigurations) {
      Object.assign(this._formBaseWidgetPayload, globalConfigurations);
    }
  }

  get formBaseWidgetPayload() {
    return this._formBaseWidgetPayload;
  }

  @Input()
  public componentToLoad: any | string;

  @Input()
  set item(item: IdynamicFormItem) {
    if(isEqual(this._item,item)) {
      return;
    }

    this._item = item;
    this.setWidgetConfig();
  }

  get item(): IdynamicFormItem {
      return this._item;
  }

  private _disabled;

  @ViewChild('widgetContent', { static: true, read: ViewContainerRef}) widgetContent;

  @Input()
  updateValue(value) {
    if (!value || typeof value !== 'object') {
      value = new WidgetValueWrapper(value);
    }

    Reflect.defineProperty(value, '__forceUpdate', { enumerable: false, configurable: true, value: true, writable: true});

    // Please, don't move 'emit' inside 'if'-operator because it prevents propagation of widget inspired events to parent form
    // Tell the form to accept those changes without distinct
    this.forceUpdateEmitter.emit(true);

    if (this.item.isRootProperty) {
      if (this.pathToField) {
        if (!this.formInstance.controls[this.pathToField]) {
          this.formInstance.addControl(this.pathToField, new FormControl());
        }

        this.formInstance.controls[this.pathToField].patchValue(value); // patch value to form root level + pathToField
      } else {
        this.formInstance.patchValue(value); // patch value to form root level
      }
    }
    else if (this.formInstance) {
      this.formInstance.controls[this.item.name].patchValue(value);
    }
  }

  constructor(
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _changeDetector: ChangeDetectorRef,
    private _elementRef: ElementRef,
    private _formHandler: FormHandlerService
  ) {
      fromEvent(this._elementRef.nativeElement, 'focusin').pipe(
          takeUntil(this._destroyer.asObservable())
      ).subscribe(this._onFocusIn.bind(this));

      fromEvent(this._elementRef.nativeElement, 'focusout').pipe(
          takeUntil(this._destroyer.asObservable())
      ).subscribe(this._onBlur.bind(this));
  }

  private _onFocusIn(e: Event) {
    this.onFocus.emit(e);
  }

  private _onBlur(e: Event) {
    this.onBlur.emit(e);
  }

  public ngAfterContentInit(): void {
    this.loadDynamicWidget();
  }

  private _addControl(formItemConfig: IdynamicFormItem) {
    this.onAddControl.emit(formItemConfig);
  }

  public loadDynamicWidget() {
    if (!this.componentToLoad) {
      this.componentToLoad = this.item.options.component;
    }

    const component = typeof this.componentToLoad === 'string' ? ResolvableService.Resolve(this.componentToLoad) : this.componentToLoad;
    const componentFactory = this._componentFactoryResolver.resolveComponentFactory(component);

    this.widgetContent.clear();

    this._componentRef = this.widgetContent.createComponent(componentFactory);

    if (this.componentToLoad === 'TreatmentSubject') {
      this._componentRef.instance.formId = this.formId;
    }

    // Setting widget callback methods
    this._componentRef.instance.addWidgetFormControl = (config: IdynamicFormItem) => {
      this._addControl.apply(this, [config]);
    };

    const originalCallbackFunction = this._componentRef.instance.widgetUpdateValueCallback;

    this._componentRef.instance.widgetUpdateValueCallback = (value) => {
      if (originalCallbackFunction) {
        originalCallbackFunction(value);
      }

      this.updateValue.apply(this, [value]);
    };

    this._componentRef.instance.widgetSubmitStatusValueCallback = (value: boolean) => {
      this.submitUpdateEmitter.emit(value);
    };

    this._componentRef.instance.forceUpdate = (state: boolean) => {
      this.forceUpdateEmitter.emit(state);
    };

    // Setting the widget config and data
    this._componentRef.instance.widgetFormInstance = this.formInstance;
    const widgetPayload = Object.assign({ }, this.formBaseWidgetPayload, this.item.widgetPayload);
    this._componentRef.instance.widgetConfig = Object.assign({ }, this.item, { widgetPayload});
    this._formHandler.getForm(get(widgetPayload, 'formId'));
    this.setComponentData();
    this.setComponentValidator();
  }

  private setWidgetConfig() {
    if (this._componentRef) {
      const widgetPayload = Object.assign({ }, this.formBaseWidgetPayload, this.item.widgetPayload);
      this._componentRef.instance.widgetConfig = Object.assign({ }, this.item, { widgetPayload});
    }
  }

  private setComponentData() {
    if (this._componentRef && !this._changeDetector['destroyed']) {
      this._componentRef.instance.widgetFormInstance = this.formInstance;

      if (this.value && isEqual(this._componentRef.instance.widgetData, this.value)) {
        return;
      }

      this._componentRef.instance.widgetData = this.value;
      this._componentRef.instance.options = this._options;
      this.setWidgetDisabledStatus();
    }
  }

  public ngOnDestroy(): void {
    this._destroyer.next();
    this._destroyer.complete();
    this._componentRef = null;
  }

  private setWidgetDisabledStatus() {
    this._disabled = this.item?.immutable ? false : this._disabled;
    if (this._componentRef) {
      this._componentRef.instance.widgetDisabledStatus = this._disabled;
      this._componentRef.instance.disabled = this._disabled;
      this._componentRef.instance.setDisabledStatus && this._componentRef.instance.setDisabledStatus(this._disabled);

      if (this._item.updateWidgetFormInstanceOnReflect) { // update form instance after reflect
        this.setWidgetFormInstance();
      }
    }
  }

  private setWidgetFormInstance() {
    if (this._componentRef) {
      this._componentRef.instance.widgetFormInstance = this._formInstance;
    }
  }

  public setWidgetSubmitted() {
    if (this._componentRef) {
      this._componentRef.instance.widgetFormInstanceSubmitted = this._submitted;
    }
  }

  private setComponentValidator() {
    if (this._componentRef.instance.widgetValidator) {
      const control  = this.item.isRootProperty
        ? this.formInstance
        : this.formInstance.controls[this.item.name];

        if(control && !control.asyncValidator) {
          const asyncValidator = new WidgetValidator(this._componentRef.instance.widgetValidator.bind(this._componentRef.instance));
          control.setAsyncValidators(asyncValidator.validate.bind(asyncValidator));
          control.updateValueAndValidity();
        }
   }
  }

}

class WidgetValidator implements AsyncValidator {
  constructor(
    private validationCallback: any
  ) {
  }

  validate(control: AbstractControl): Promise<ValidationErrors> | Observable<ValidationErrors> {
    return  this.validationCallback()
      ? of (null)
      : of ({ error: true});
  }

}

export interface IWidgetAction {
  action: string;
  data: any;
  source: string;
}
